import { useCallback, useEffect, useState } from 'react';
import { ExperimentSlim, playbookBankService, VariantType } from 'src/services';
import { useAppSelector } from 'src/hooks';
import { getProductName } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { experimentActionButtonsActionMethods } from '../../ExperimentActionButtons/Actions';
import { variantsTableActionMethods } from '../../ExperimentForm/VariantsTable';
import { getMetricLabel } from '../WhatToTestStep/constants';
import { buildSensitivityCopy } from '../GoalStep/constants';
import { PREFLIGHT_CHECK_LABELS } from './constants';
import { PreflightCheck, ReviewStepProps, UseReviewStepReturnType } from './types';

export const useReviewStep = ({
  shopId,
  experiment,
  dispatch,
  runningExperimentId,
  onLaunched,
  onClose,
}: ReviewStepProps): UseReviewStepReturnType => {
  const wizardSelection = experiment?.wizardSelection;
  const controlVariant = experiment?.variants.find(variant => variant.type === VariantType.Control);
  const testVariant = experiment?.variants.find(variant => variant.type === VariantType.Test);

  const [playbookSatisfied, setPlaybookSatisfied] = useState(true);
  const [showUseUuidDialog, setShowUseUuidDialog] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | undefined>();

  const comparedVariantId = useAppSelector(state => state.experiments.comparedVariantId);

  useEffect(() => {
    let cancelled = false;

    if (wizardSelection?.mode !== 'playbooks' || !wizardSelection.selectedPlaybookId) {
      setPlaybookSatisfied(true);
      return undefined;
    }

    playbookBankService
      .getPlaybookBank({ shopId })
      .then(bank => {
        if (cancelled) return;
        const playbook = bank.find(item => item.id === wizardSelection.selectedPlaybookId);
        setPlaybookSatisfied(playbook?.satisfied ?? true);
      })
      .catch(() => {
        if (!cancelled) setPlaybookSatisfied(true);
      });

    return () => {
      cancelled = true;
    };
  }, [shopId, wizardSelection?.mode, wizardSelection?.selectedPlaybookId]);

  const getPreflightChecks = useCallback((): PreflightCheck[] => {
    const total =
      (experiment?.productionTrafficAllocation ?? 0) +
      (controlVariant?.trafficAllocation ?? 0) +
      (testVariant?.trafficAllocation ?? 0);
    const isSplitValid = total === 100;

    const hasConflictingExperiment = !!(
      experiment &&
      runningExperimentId &&
      runningExperimentId !== experiment.id
    );

    const changesCount = testVariant?.versionChangesSummary?.totalChanges ?? 0;

    return [
      {
        id: 'required-data-fields',
        ...PREFLIGHT_CHECK_LABELS['required-data-fields'],
        status: playbookSatisfied ? 'pass' : 'blocked',
      },
      {
        id: 'no-conflicting-experiment',
        ...PREFLIGHT_CHECK_LABELS['no-conflicting-experiment'],
        status: hasConflictingExperiment ? 'blocked' : 'pass',
      },
      {
        id: 'allocation-totals-100',
        ...PREFLIGHT_CHECK_LABELS['allocation-totals-100'],
        status: isSplitValid ? 'pass' : 'blocked',
      },
      {
        id: 'catalogue-freshness',
        ...PREFLIGHT_CHECK_LABELS['catalogue-freshness'],
        status: 'pass',
      },
      {
        id: 'traffic-sufficient',
        ...PREFLIGHT_CHECK_LABELS['traffic-sufficient'],
        status: experiment?.estimatorInputs ? 'pass' : 'warn',
      },
      {
        id: 'single-lever',
        ...PREFLIGHT_CHECK_LABELS['single-lever'],
        status: changesCount > 1 ? 'warn' : 'pass',
        description:
          changesCount > 1
            ? `${changesCount} changes in Variant B. You will know if B won, not why.`
            : PREFLIGHT_CHECK_LABELS['single-lever'].description,
      },
    ];
  }, [experiment, controlVariant, testVariant, runningExperimentId, playbookSatisfied]);

  const preflightChecks = getPreflightChecks();
  const canLaunch =
    !!experiment &&
    preflightChecks.every(check => check.status !== 'blocked') &&
    (!runningExperimentId || runningExperimentId === experiment.id);

  const onLaunchClick = useCallback((): void => {
    setLaunchError(undefined);
    const freshChecks = getPreflightChecks();
    const stillLaunchable =
      !!experiment &&
      freshChecks.every(check => check.status !== 'blocked') &&
      (!runningExperimentId || runningExperimentId === experiment.id);

    if (!stillLaunchable) {
      setLaunchError('A blocking check failed. Fix it and try again.');
      return;
    }

    setShowUseUuidDialog(true);
  }, [experiment, getPreflightChecks, runningExperimentId]);

  const onConfirmLaunch = useCallback(async (): Promise<void> => {
    if (!experiment) return;

    setShowUseUuidDialog(false);
    setIsLaunching(true);
    setLaunchError(undefined);

    try {
      const updatedExperiment = (await dispatch(
        experimentActionButtonsActionMethods.runExperiment({
          shopId,
          id: experiment.id,
          experiment,
        })
      )) as unknown as ExperimentSlim | undefined;
      if (updatedExperiment) {
        onLaunched(experiment.id);
      } else {
        setLaunchError('Launch failed — the experiment stays a draft. Please try again.');
      }
    } finally {
      setIsLaunching(false);
    }
  }, [dispatch, experiment, onLaunched, shopId]);

  const onSaveAsDraft = useCallback((): void => {
    onClose();
  }, [onClose]);

  const onOpenFullDiff = useCallback((): void => {
    if (testVariant) {
      dispatch(variantsTableActionMethods.compareVariant({ variantId: testVariant.id }));
    }
  }, [dispatch, testVariant]);

  const surface = wizardSelection?.levers?.[0]?.surface;
  const surfaceLabel = surface ? getProductName(surface) : 'All surfaces';
  const metricLabel = experiment?.metric ? getMetricLabel(experiment.metric) : '—';
  const splitLabel =
    experiment?.productionTrafficAllocation != null && controlVariant && testVariant
      ? `${experiment.productionTrafficAllocation} / ${controlVariant.trafficAllocation} / ${testVariant.trafficAllocation}`
      : '—';
  const minimumDurationLabel =
    experiment?.estimatorInputs?.estimatedDays != null
      ? `${experiment.estimatorInputs.estimatedDays} days`
      : '—';
  const sensitivity = experiment?.estimatorInputs
    ? buildSensitivityCopy(
        metricLabel,
        experiment.estimatorInputs.dailyTestUsers,
        experiment.estimatorInputs.mde
      )
    : 'Sensitivity will show once traffic and MDE are set in the Traffic step.';

  return {
    summary: {
      name: experiment?.name || 'Untitled experiment',
      hypothesis: experiment?.hypothesis || '',
      sensitivity,
      surfaceLabel,
      metricLabel,
      splitLabel,
      minimumDurationLabel,
    },
    preflightChecks,
    canLaunch,
    isLaunching,
    launchError,
    showUseUuidDialog,
    setShowUseUuidDialog,
    onLaunchClick,
    onConfirmLaunch,
    onSaveAsDraft,
    comparedVariantId,
    onOpenFullDiff,
  };
};
